import { IF, nil, NO, UITextView, UIView } from "uicore-ts"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHClipboard } from "../../Custom components/RH/Utils_slop/RHClipboard"
import { RHCPVCodeFormatter } from "../../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import { RHColors, RHFont, RHRadius, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCPVSummationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import type { RHCPVSummationInputPart, RHCPVSummationLevelResult, RHCPVSummationReport, RHCPVSummationSpendItem } from "../../SocketClientFunctions"
import { CPVSummationContractGroupView, type CPVSummationContractGroup } from "./CPVSummationContractGroupView"

export class CPVSummationDetailsView extends UIView {

    readonly titleLabel = new UITextView(undefined, UITextView.type.header5)
        .configuredWithObject({ text: "Arvestuse jaotus", isSingleLine: NO, textColor: RHColors.textPrimary })
        .addedAsSubviewToView(this)

    readonly summaryLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textSecondary,
        style: { fontSize: RHFont.sizeSmall, lineHeight: "1.5" }
    }).addedAsSubviewToView(this)

    readonly copyButton = new RHButton().configuredWithObject({
        variant: "light", contentPadding: RHSpacing.small,
        titleLabel: { text: "Kopeeri arvestus", style: { fontSize: RHFont.sizeSmall } }
    }).addedAsSubviewToView(this)

    readonly previousContractsLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textPrimary,
        style: { fontSize: RHFont.sizeSmall, lineHeight: "1.5" }
    })

    readonly currentProcurementLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textPrimary,
        style: { fontSize: RHFont.sizeSmall, lineHeight: "1.5" }
    })

    readonly totalLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textPrimary,
        style: { fontSize: RHFont.sizeSmall, fontWeight: RHFont.weightSemibold, lineHeight: "1.5" }
    })

    readonly thresholdLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textPrimary,
        style: { fontSize: RHFont.sizeSmall, fontWeight: RHFont.weightSemibold, lineHeight: "1.5" }
    })

    readonly amountsContainerView = new UIView().configuredWithObject({
        backgroundColor: RHColors.surfaceCool,
        style: {
            border: `1px solid ${RHColors.borderSlate.stringValue}`,
            borderRadius: `${RHRadius.medium}px`
        },
        layoutSubviews: () => this._layoutAmountLabels()
    }).addedAsSubviewToView(this)

    readonly contractsTitleLabel = new UITextView().configuredWithObject({
        isSingleLine: NO, textColor: RHColors.textPrimary,
        style: { fontWeight: RHFont.weightSemibold, lineHeight: "1.5" }
    }).addedAsSubviewToView(this)

    readonly calculationNoteLabel = new UITextView().configuredWithObject({
        text: "Arvestus sisaldab valitud CPV-haru alla kuuluvaid hankija viimase 12 kuu jooksul sõlmitud lepinguid ning selle hanke samasse harusse kuuluvaid osi. Lepingute puhul kasutatakse tegelikku maksumust ja selle hanke puhul eeldatavat maksumust. Kõik summad on käibemaksuta. Riigihangete registrist andmeid ei impordita.",
        isSingleLine: NO, textColor: RHColors.textSecondary,
        style: { fontSize: RHFont.sizeSmall, lineHeight: "1.5" }
    }).addedAsSubviewToView(this)

    _report?: RHCPVSummationReport
    _level?: RHCPVSummationLevelResult
    _groups: CPVSummationContractGroup[] = []
    _groupViews: CPVSummationContractGroupView[] = []

    constructor(elementID?: string) {
        super(elementID)
        this.previousContractsLabel.addedAsSubviewToView(this.amountsContainerView)
        this.currentProcurementLabel.addedAsSubviewToView(this.amountsContainerView)
        this.totalLabel.addedAsSubviewToView(this.amountsContainerView)
        this.thresholdLabel.addedAsSubviewToView(this.amountsContainerView)
        this.copyButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            const didCopy = await RHClipboard.copy(this.textForCopying())
            this.copyButton.titleLabel.text = IF(didCopy)(() => "Kopeeritud").ELSE(() => "Kopeerimine ebaõnnestus")
        }
    }

    updateWithReportForLevel(report: RHCPVSummationReport, level: RHCPVSummationLevelResult) {
        this.clearReport()
        this._report = report
        this._level = level
        this._groups = CPVSummationDetailsView.groupsByCPVForContractItems(level.items)
        this.titleLabel.text = "Arvestuse jaotus"
        this.summaryLabel.text = `${RHTextSanitizer.escapedHTML(level.cpvLabel || "Valitud CPV-haru")} · ` +
            `Arvestusperiood: ${CPVSummationContractGroupView.estonianDateTextForISODate(report.windowStart)} – ` +
            CPVSummationContractGroupView.estonianDateTextForISODate(report.windowEnd)
        this.previousContractsLabel.text = `Viimase 12 kuu lepingud: ${RHEuroFormatter.formattedAmount(level.previousSum)}`
        this.currentProcurementLabel.text = `Selle hanke eeldatav maksumus: ${RHEuroFormatter.formattedAmount(level.newSum)}`
        this.totalLabel.text = `Kogusumma: ${RHEuroFormatter.formattedAmount(level.sumWithNew)}`
        this.thresholdLabel.text = `Rakenduv piirmäär: ${RHEuroFormatter.formattedAmount(level.threshold)}`
        this.contractsTitleLabel.text = IF(level.items.length > 0)(
            () => `Arvestusse kaasatud lepingud (${level.items.length})`
        ).ELSE(
            () => "Viimase 12 kuu jooksul selles CPV-harus sõlmitud lepinguid ei ole."
        )
        const displayedGroups = this._groups.slice(0, 5)
        if (this._groups.length > 5) {
            const remainingGroups = this._groups.slice(5)
            displayedGroups.push({
                title: `Ülejäänud ${remainingGroups.length} koodi`,
                amount: remainingGroups.reduce((sum, group) => sum + group.amount, 0),
                percentage: remainingGroups.reduce((sum, group) => sum + group.percentage, 0),
                items: remainingGroups.flatMap(group => group.items).sort((first, second) => second.amountExclVat - first.amountExclVat)
            })
        }
        this._groupViews = displayedGroups.map(group => {
            const groupView = new CPVSummationContractGroupView()
            groupView.updateWithGroup(group)
            return groupView.addedAsSubviewToView(this)
        })
        this.setNeedsLayoutUpToRootView()
    }

    clearReport() {
        this._report = undefined
        this._level = undefined
        this._groups = []
        this._groupViews.forEach(view => view.removeFromSuperview())
        this._groupViews = []
        this.titleLabel.text = "Arvestuse jaotus"
        this.summaryLabel.text = ""
        this.previousContractsLabel.text = ""
        this.currentProcurementLabel.text = ""
        this.totalLabel.text = ""
        this.thresholdLabel.text = ""
        this.contractsTitleLabel.text = ""
        this.copyButton.titleLabel.text = "Kopeeri arvestus"
    }

    static groupsByCPVForContractItems(items: RHCPVSummationSpendItem[]): CPVSummationContractGroup[] {
        const groupsByCPV = new Map<string, CPVSummationContractGroup>()
        const historicalAmount = items.reduce((sum, item) => sum + item.amountExclVat, 0)
        for (const item of items) {
            let group = groupsByCPV.get(item.cpv8)
            if (!group) {
                group = { title: item.cpvLabel || "Nimetus puudub", amount: 0, percentage: 0, items: [] }
                groupsByCPV.set(item.cpv8, group)
            }
            group.items.push(item)
            group.amount += item.amountExclVat
        }
        const groups = [...groupsByCPV.values()]
        for (const group of groups) {
            if (historicalAmount > 0) {
                group.percentage = group.amount / historicalAmount * 100
            }
            group.items.sort((first, second) => second.amountExclVat - first.amountExclVat)
        }
        return groups.sort((first, second) => second.amount - first.amount || first.title.localeCompare(second.title, "et"))
    }

    textForCopying(): string {
        if (!this._report || !this._level) {
            return ""
        }
        const level = this._level
        const lines = [
            level.cpvLabel || "Valitud CPV-haru",
            `Arvestusperiood: ${CPVSummationContractGroupView.estonianDateTextForISODate(this._report.windowStart)} – ` +
                CPVSummationContractGroupView.estonianDateTextForISODate(this._report.windowEnd),
            `Viimase 12 kuu lepingud: ${RHEuroFormatter.formattedAmount(level.previousSum)}`,
            `Selle hanke eeldatav maksumus: ${RHEuroFormatter.formattedAmount(level.newSum)}`,
            `Kogusumma: ${RHEuroFormatter.formattedAmount(level.sumWithNew)}`,
            `Rakenduv piirmäär: ${RHEuroFormatter.formattedAmount(level.threshold)}`
        ]
        for (const group of this._groups) {
            lines.push(`${group.title} · ${CPVSummationContractGroupView.summaryTextForGroup(group)}`)
            for (const item of group.items) {
                lines.push(`  ${CPVSummationContractGroupView.estonianDateTextForISODate(item.commitmentDate)} · ${CPVSummationContractGroupView.titleForContractItem(item)} · ${RHEuroFormatter.formattedAmount(item.amountExclVat)}`)
            }
        }
        lines.push(this.calculationNoteLabel.text)
        return lines.join("\n")
    }

    _layoutAmountLabels() {
        const bounds = this.amountsContainerView.contentBoundsWithInset(RHSpacing.small)
        const labels = [
            this.previousContractsLabel,
            this.currentProcurementLabel,
            this.totalLabel,
            this.thresholdLabel
        ]
        if (bounds.width < RHCPVSummationViewTokens.amountSummaryTwoColumnMinimumWidth) {
            bounds.framesByDistributingViewsAsColumn(labels, RHSpacing.tiny)
            return
        }
        const firstRowFrames = bounds.rectanglesBySplittingWidth([1, 1], RHSpacing.medium)
        const firstRowHeight = Math.max(
            this.previousContractsLabel.intrinsicContentHeight(firstRowFrames[0].width),
            this.currentProcurementLabel.intrinsicContentHeight(firstRowFrames[1].width)
        )
        const measuredFirstRowFrames = [
            firstRowFrames[0].rectangleWithHeight(firstRowHeight).assignedAsFrameOfView(this.previousContractsLabel),
            firstRowFrames[1].rectangleWithHeight(firstRowHeight).assignedAsFrameOfView(this.currentProcurementLabel)
        ]
        const secondRowFrames = measuredFirstRowFrames.boundingBox()
            .rectangleForNextRow(RHSpacing.tiny)
            .rectanglesBySplittingWidth([1, 1], RHSpacing.medium)
        const secondRowHeight = Math.max(
            this.totalLabel.intrinsicContentHeight(secondRowFrames[0].width),
            this.thresholdLabel.intrinsicContentHeight(secondRowFrames[1].width)
        )
        secondRowFrames[0].rectangleWithHeight(secondRowHeight).assignedAsFrameOfView(this.totalLabel)
        secondRowFrames[1].rectangleWithHeight(secondRowHeight).assignedAsFrameOfView(this.thresholdLabel)
    }

    static levelsForPartInReport(part: RHCPVSummationInputPart, report: RHCPVSummationReport): RHCPVSummationLevelResult[] {
        const cpv8 = RHCPVCodeFormatter.normalizedCPV8FromText(part.cpv9)
        const path = report.pathsByCPV[cpv8] ?? []
        return report.levels.filter(level => path.includes(level.cpvAtLevel))
    }

    static levelWithHighestRatio(levels: readonly RHCPVSummationLevelResult[]): RHCPVSummationLevelResult | undefined {
        let strongestLevelResult: RHCPVSummationLevelResult | undefined
        for (const levelResult of levels) {
            if (!strongestLevelResult || levelResult.ratio > strongestLevelResult.ratio) {
                strongestLevelResult = levelResult
            }
        }
        return strongestLevelResult
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        const titleRowHeight = Math.max(
            RHCPVSummationViewTokens.headerActionHeight,
            this.titleLabel.intrinsicContentHeight(
                bounds.width - RHCPVSummationViewTokens.copyButtonWidth - RHSpacing.medium
            )
        )
        bounds
            .rectangleWithHeight(titleRowHeight)
            .distributeViewsAlongWidth(
                [this.titleLabel, this.copyButton],
                undefined,
                RHSpacing.medium,
                [nil, RHCPVSummationViewTokens.copyButtonWidth]
            )
            .rectangleForNextRow(RHSpacing.small, this.summaryLabel)
            .assignedAsFrameOfView(this.summaryLabel)
            .rectangleForNextRow(RHSpacing.medium, this.amountsContainerView)
            .assignedAsFrameOfView(this.amountsContainerView)
            .rectangleForNextRow(RHSpacing.medium, this.contractsTitleLabel)
            .assignedAsFrameOfView(this.contractsTitleLabel)
            .IF(this._groupViews.length > 0)
            .rectangleForNextRow(RHSpacing.small, 0)
            .framesByDistributingViewsAsColumn(this._groupViews, RHSpacing.tiny)
            .ENDIF(frames => {
                if (Array.isArray(frames)) {
                    return frames.boundingBox()
                }
                return frames
            })
            .rectangleForNextRow(RHSpacing.small, this.calculationNoteLabel)
            .assignedAsFrameOfView(this.calculationNoteLabel)
    }
}
